import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Helper } from '../../helpers/helper.service';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { throwError, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Login } from '../../auth/model/Login';
import { BackofficeRole } from '../../back-office/Models/BackofficeRole';
import { Country } from '../../back-office/Models/Country';
import { DealerRole } from '../../dealer-system/Models/DealerRole';
import { LanguageModel } from '../../translation/translation.models';
import { ResetPassword } from '../Models/ResetPassword';
import { Role } from '../Models/Role';
import { CookieManagerService } from './cookie.service';
import { CookieService } from 'ngx-cookie-service';
import { ExactExpireResponse } from '../Models/ExactExpireResponse';

interface LoginRefreshToken {
  refreshToken: string;
  valid: string;
}

interface LoginResponse {
  token: string;
  refreshToken: LoginRefreshToken;
  roles: string;
  avatar: any;
  userRole: string;
  route: string;
  language: LanguageModel;
  username: string;
  fullName: string;
  companyName?: string;
  dealerPoints: string;
  dealerPointEnabled: any;
  role: DealerRole | BackofficeRole | null;
  country: Country;
  status: string;
}

interface RefreshTokenResponse {
  token: string;
  refresh_token: string;
}

@Injectable()
export class AuthenticationService {

  private static convertPHPFormatToMoment(format) {
    switch (format) {
      case 'd-m-Y':
        return 'DD-MM-YYYY';
      case 'm-d-Y':
        return 'MM-DD-YYYY';
      case 'Y-m-d':
        return 'YYYY-MM-DD';
    }
    return 'YYYY-MM-DD';
  }

  public getDateFormat() {
    const dateFormat = this.cookieService.get('userDateFormat');
    return dateFormat ? dateFormat : 'YYYY-MM-DD';
  }

  constructor(
    private http: HttpClient,
    public helper: Helper,
    private reCaptchaV3Service: ReCaptchaV3Service,
    private translate: TranslateService,
    private cookieManager: CookieManagerService,
    private cookieService: CookieService
  ) {
    const logoutTimer = setTimeout(function () {
      sessionStorage.clear();
    }, (10 * 60 * 1000));
    clearTimeout(logoutTimer);
  }

  public resetPassword(data: ResetPassword, token: string): Observable<any> {
    return this.http.post(environment.apiUrl + '/users/reset-password?token=' + token, (data))
      .pipe(
        map(function (res) {
          return res;
        }),
        catchError(response => {
          this.helper.catchError(response);
          return throwError(response);
        })
      );
  }

  public checkForgotPasswordKey(key: string): Observable<any> {
    return this.http.get(environment.apiUrl + '/users/reset-password/validate/' + key)
      .pipe(
        catchError(res => this.helper.catchError(res))
      );
  }

  public getProfile(): Observable<any> {
    return this.http.get(environment.apiUrl + '/users/profile')
      .pipe(
        catchError(res => this.helper.catchError(res))
      );
  }

  private encodeRole(role: DealerRole | BackofficeRole | null): string {
    return btoa(JSON.stringify(role));
  }

  public login(credentials: Login): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', 'Basic ' + btoa(credentials.username + ':' + credentials.password));
    return this.http.post<LoginResponse>(environment.apiUrl + '/users/login', {
      'token': credentials.recaptchaToken,
      'auth_code': credentials.auth_code
    }, {
      'headers': headers,
    })
      .pipe(
        tap(res => {
          if (res['2faIsActivated']) {
            return res;
          } else {
            const dateFormat = AuthenticationService.convertPHPFormatToMoment(res.country ? res.country.userDateFormat : 'd-m-Y');
            const dealerRole = this.encodeRole(res.roles[0] === Role.ROLE_DEALER ? res.role : null);
            const backofficeRole = this.encodeRole(res.roles[0] === Role.ROLE_BACK_OFFICE ? res.role : null);
            // @ts-ignore
            this.addCookies(
              res.token,
              res.refreshToken.refreshToken,
              res.roles[0],
              credentials.rememberMe,
              res.refreshToken.valid,
              res.language.locale,
              res.username,
              res.fullName,
              res.companyName || '',
              dealerRole,
              backofficeRole,
              res.dealerPoints,
              res.dealerPointEnabled,
              res.country ? res.country.thousandSeparator : '.',
              res.country ? res.country.decimalSeparator : ',',
              res.country ? res.country.currencyDecimals + '' : '2',
              dateFormat,
              res.country ? res.country.dealerVat + '' : '0',
              res.country ? res.country.customerVat + '' : '0',
              res.status
            );
            return res;
          }
        }),
        catchError(res => this.helper.catchError(res))
      );

  }

  public checkPassword(password): Observable<any> {
    return this.reCaptchaV3Service.execute('login').pipe(
      switchMap((token: string) => {
        let headers = new HttpHeaders();
        headers = headers.set('Authorization', 'Basic ' + btoa(this.getUserName() + ':' + password));
        return this.http.post<LoginResponse>(environment.apiUrl + '/users/login', {
          'token': token
        }, {
          'headers': headers,
        });
      }),
      catchError(() => {
        this.helper.popMessageDialog(
          this.translate.instant('errors.recaptcha.title'),
          this.translate.instant('errors.recaptcha.failed'),
          'error',
          false,
        );
        return throwError(this.translate.instant('errors.recaptcha.failed'));
      })
    );
  }

  public refreshToken(): Observable<RefreshTokenResponse> {
    return this.http.post<RefreshTokenResponse>(environment.apiUrl + '/token/refresh', {
      'refresh_token': this.getRefreshToken(),
    }).pipe(
      tap(res => {
        this.setRefreshToken(res.refresh_token);
        this.setToken(res.token);
        return res;
      }),
      catchError(res => throwError(res))
    );
  }

  /**
   * Add data to user cookie
   * @param token
   * @param accessToken
   * @param role
   * @param rememberMe
   * @param tokenExpireDate
   * @param locale
   * @param userName
   * @param fullName
   * @param companyName
   * @param dealerRole
   * @param dealerPoints
   * @param dealerPointEnabled
   * @param thousandSeparator
   * @param decimalSeparator
   * @param currencyDecimals
   * @param userDateFormat
   * @param dealerVat
   * @param customerVat
   */
  private addCookies(
    accessToken: string,
    refreshToken: string,
    role: string,
    rememberMe: boolean,
    tokenExpireDate: string,
    locale: string,
    userName: string,
    fullName: string,
    companyName: string,
    dealerRole,
    backofficeRole,
    dealerPoints: string,
    dealerPointEnabled: string,
    thousandSeparator: string,
    decimalSeparator: string,
    currencyDecimals: string,
    userDateFormat: string,
    dealerVat: string,
    customerVat: string,
    status,
  ) {
    if (Helper.isDefined(accessToken)) {
      this.cookieManager.setCookieExpireDate(rememberMe, tokenExpireDate);
      this.cookieManager.setCookie('token', accessToken);
      this.cookieManager.setCookie('refreshToken', refreshToken);
      this.cookieManager.setCookie('role', role);
      this.cookieManager.setCookie('username', userName);
      this.cookieManager.setCookie('locale', locale);
      this.cookieManager.setCookie('fullName', fullName);
      this.cookieManager.setCookie('companyName', companyName);
      this.cookieManager.setCookie('dealerRole', dealerRole);
      this.cookieManager.setCookie('backofficeRole', backofficeRole);
      this.cookieManager.setCookie('dealerPoints', dealerPoints);
      this.cookieManager.setCookie('dealerPointEnabled', dealerPointEnabled);
      this.cookieManager.setCookie('thousandSeparator', thousandSeparator);
      this.cookieManager.setCookie('decimalSeparator', decimalSeparator);
      this.cookieManager.setCookie('currencyDecimals', currencyDecimals);
      this.cookieManager.setCookie('userDateFormat', userDateFormat);
      this.cookieManager.setCookie('dealerVat', dealerVat);
      this.cookieManager.setCookie('customerVat', customerVat);
      this.cookieManager.setCookie('status', status);
    }
  }

  public isLoggedIn(): boolean {
    const cookieNotExist = this.getToken() === null || this.getToken() === '';
    return !cookieNotExist;
  }

  public getToken(): string {
    return this.cookieService.get('token');
  }

  public getBearerHeader(token?: string): string {
      return 'Bearer ' + (token || this.getToken());
  }

  public setToken(token: string) {
    this.cookieManager.setCookie('token', token);
  }

  public getRefreshToken(): string {
    return this.cookieService.get('refreshToken');
  }

  public setRefreshToken(refreshToken: string) {
    this.cookieManager.setCookie('refreshToken', refreshToken);
  }

  public getRole(): string {
    return this.cookieService.get('role');
  }

  public getUserName(): string {
    return this.cookieService.get('username');
  }

  public getLocale(): string {
    return this.cookieService.get('locale') ? this.cookieService.get('locale') : 'nl';
  }

  public getFullName(): string {
    return (this.cookieService.get('fullName') !== ' ') ? this.cookieService.get('fullName') : this.getUserName();
  }

  public getCompanyName(): string {
    return (this.cookieService.get('companyName') !== ' ') ? this.cookieService.get('companyName') : this.getFullName();
  }

  public getDealerPoints() {
    return this.cookieService.get('dealerPoints');
  }

  public getStatus() {
    return this.cookieService.get('status');
  }

  public requestUpdatePoints() {
    this.http.get(environment.apiUrl + '/users/dealer-point')
      .subscribe((resp) => {
        // @ts-ignore
        this.cookieManager.setCookie('dealerPoints', resp.dealerPoints);
      });
  }

  public isDealer(): boolean {
    return this.cookieService.get('role') === Role.ROLE_DEALER;
  }

  public isBackOffice(): boolean {
    return this.cookieService.get('role') === Role.ROLE_BACK_OFFICE;
  }

  public logout() {
    return this.http.post(environment.apiUrl + '/users/logout', null)
      .pipe(
        tap(() => {
          this.cookieManager.deleteAllCookies();
          sessionStorage.clear();
        }),
        catchError(error => {
          this.helper.catchError(error);
          return throwError(() => error);
        })
      );
  }

  public requestForgotPassword(username: string, recaptchaToken: string): Observable<any> {
    return this.http.post(environment.apiUrl + '/users/forgot-password', {
      'username': username,
      'token': recaptchaToken,
    }).pipe(
      catchError(res => {
        this.helper.catchError(res);
        return throwError(() =>res);
      })
    );
  }

  public isDealerAdmin() {
    const role = this.cookieService.check('dealerRole') ? JSON.parse(atob(this.cookieService.get('dealerRole'))) : null;
    return role && role.name === DealerRole.ROLE_ADMIN;
  }

  public isDealerModerator() {
    const role = this.cookieService.check('dealerRole') ? JSON.parse(atob(this.cookieService.get('dealerRole'))) : null;
    return role && role.name === DealerRole.ROLE_MODERATOR;
  }

  public isDealerUser() {
    const role = this.cookieService.check('dealerRole') ? JSON.parse(atob(this.cookieService.get('dealerRole'))) : null;
    return role && role.name === DealerRole.ROLE_USER;
  }

  public isDealerTabletUser() {
    const role = this.cookieService.check('dealerRole') ? JSON.parse(atob(this.cookieService.get('dealerRole'))) : null;
    return role && role.name === DealerRole.ROLE_TABLET;
  }

  public isBackofficeAdmin() {
    const role = this.cookieService.check('backofficeRole') ? JSON.parse(atob(this.cookieService.get('backofficeRole'))) : null;
    return role && role.name === BackofficeRole.ROLE_ADMIN;
  }

  public isBackofficeSuperadmin() {
    const role = this.cookieService.check('backofficeRole') ? JSON.parse(atob(this.cookieService.get('backofficeRole'))) : null;
    return role && role.name === BackofficeRole.ROLE_SUPERADMIN;
  }

  public getDealerVAT(): string {
    return (this.cookieService.get('dealerVat') !== ' ') ? this.cookieService.get('dealerVat') : '0';
  }

  public getCustomerVAT(): string {
    return (this.cookieService.get('customerVat') !== ' ') ? this.cookieService.get('customerVat') : '0';
  }

  public getExactConnectionStatus() {
    return this.http.get(environment.apiUrl + '/backoffice/exact/connection');
  }

  public getExactExpireAt() {
    return this.http.get<ExactExpireResponse>(environment.apiUrl + '/backoffice/exact/expire_at');
  }

  public isDealerPointEnabled() {
    return this.cookieService.get('dealerPointEnabled') === 'true';
  }
}
